// src/components/Header.jsx
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const location = useLocation();

  // --- Scroll Logic for Hide/Show ---
  useEffect(() => {
    const controlNavbar = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > 70) {
        // Scrolling DOWN and past the top area -> Hide
        setIsVisible(false);
        setOpenDropdown(null); // Close dropdowns when hiding
      } else {
        // Scrolling UP -> Show
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", controlNavbar);
    return () => window.removeEventListener("scroll", controlNavbar);
  }, [lastScrollY]);

  const navGroups = [
    {
      title: "Company", items: [
        { name: "About Us", path: "/about" },
        { name: "Gallery", path: "/gallery" },
        { name: "Student Reviews", path: "/reviews" },
      ]
    },
    {
      title: "Study Abroad", items: [
        { name: "Admissions Guidance", path: "/admissions" },
        { name: "Partner Universities", path: "/partnered-universities" },
        { name: "Visa Success Stories", path: "/visa-success-stories" },
      ]
    },
    {
      title: "Training", items: [
        { name: "All Courses", path: "/courses" },
        { name: "IELTS Mock Test", path: "/book-ielts-mock-test" },
        { name: "Success Stories", path: "/success-stories" },
      ]
    },
  ];

  return (
    <>
      <header
        className={`fixed inset-x-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-md transition-transform duration-300 ease-in-out ${isVisible ? "translate-y-0" : "-translate-y-full"
          } ${/* If top bar scrolls away, we want this to stick to top 0 usually, but logic handles visibility */ ""}`}
        style={{ top: 0 }}
      >
        <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-24">
          {/* Reduced height from h-16 to h-14 */}
          <div className="flex items-center justify-between h-24">

            {/* Logo - Smaller (h-8) */}
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.jpg" alt="6th Sense" className="h-14 w-auto object-contain" />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-7">
              <Link to="/" className={`text-base font-bold transition ${location.pathname === "/" ? "text-red-600" : "text-slate-700 hover:text-red-600"}`}>
                Home
              </Link>
              {navGroups.map((group, idx) => (
                <div
                  key={idx}
                  className="relative h-14 flex items-center" // Ensure full height for hover area
                  onMouseEnter={() => setOpenDropdown(idx)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button className="text-base font-extrabold flex items-center gap-1 transition focus:outline-none">
                    {group.title}
                    <ChevronDown size={14} className={`transition-transform duration-200 ${openDropdown === idx ? "rotate-180 text-red-600" : ""}`} />
                  </button>

                  {/* Dropdown Menu */}
                  <div className={`absolute top-full left-1/2 -translate-x-1/2 w-56 bg-white rounded-b-lg shadow-xl border-x border-b border-slate-100 overflow-hidden transition-all duration-200 ${openDropdown === idx ? "opacity-100 translate-y-0 visible" : "opacity-0 -translate-y-2 invisible"}`}>
                    {group.items.map((item) => (
                      <Link key={item.path} to={item.path} className="block px-4 py-2.5 text-sm text-slate-600 hover:text-red-600 hover:bg-red-50 transition border-l-2 border-transparent hover:border-red-600">
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
              <Link to="/contact" className={`text-base font-bold transition ${location.pathname === "/contact" ? "text-red-600" : "text-slate-700 hover:text-red-600"}`}>
                Contact
              </Link>
            </div>

            {/* CTA Buttons - Slightly smaller padding for compact look */}
            <div className="hidden md:flex items-center gap-4">
              <Link to="/book-ielts-mock-test" className="text-sm font-bold text-slate-700 hover:text-red-600 transition">
                Mock Test
              </Link>
              <Link to="/book-free-counselling" className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-lg shadow-md transition-all hover:shadow-lg hover:-translate-y-0.5">
                Free Counselling
              </Link>
            </div>

            {/* Mobile Toggle */}
            <button onClick={() => setIsOpen(!isOpen)} className="lg:hidden p-2 text-slate-800 hover:bg-slate-100 rounded-lg transition">
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden bg-white border-t border-slate-200 shadow-inner max-h-[80vh] overflow-y-auto">
            <div className="px-4 py-5 space-y-3">
              <Link to="/" onClick={() => setIsOpen(false)} className="block text-base font-semibold text-slate-800 py-2">
                Home
              </Link>

              {navGroups.map((group, idx) => (
                <div key={idx} className="border-b border-slate-100 last:border-0 pb-3">
                  <button
                    onClick={() => setOpenDropdown(openDropdown === idx ? null : idx)}
                    className="flex w-full justify-between items-center text-base font-semibold text-slate-800 py-2"
                  >
                    {group.title}
                    <ChevronDown size={18} className={`transition-transform ${openDropdown === idx ? "rotate-180 text-red-600" : ""}`} />
                  </button>
                  {openDropdown === idx && (
                    <div className="mt-1 space-y-1 pl-3 bg-slate-50 rounded-md p-2">
                      {group.items.map((item) => (
                        <Link key={item.path} to={item.path} onClick={() => setIsOpen(false)} className="block py-2 text-sm text-slate-600 hover:text-red-600 font-medium">
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}


                </div>
              ))}

              <Link to="/contact" className={"block text-base font-semibold text-slate-800 py-2"} onClick={() => setIsOpen(false)}>
                    Contact
              </Link>

              <div className="pt-4 space-y-3">
                <Link to="/book-free-counselling" onClick={() => setIsOpen(false)} className="w-full block text-center py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition text-sm">
                  Book Free Counselling
                </Link>
                <Link to="/book-ielts-mock-test" onClick={() => setIsOpen(false)} className="w-full block text-center py-2.5 border border-slate-300 hover:bg-slate-50 font-bold rounded-lg transition text-sm">
                  Book IELTS Mock Test
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}